
import {defineComponent, PropType} from "vue";
import campaignBrandingManager from "@/views/survey/ts/campaignBrandingManager";
import {Campaign} from "@/models/campaign";
import {RecordingManager, RecordingUnavailableAnotherAppRecording, RecordingUnavailableBrowserNoMicrophoneError, RecordingUnavailableDenied, RecordingUnavailableMicrophoneIsMuted, RecordingUnavailableNoCapabilitiesError} from "@/views/survey/ts/recordingManager";
import {ConversationalSurveyQuestionUpdated, ConversationalSurveyRound, ConversationalSurveyService} from "@/utils/conversationalSurveyService";
import BackgroundImageMixin from "@/components/recorder/BackgroundImageMixin.vue";
import {mapActions} from "pinia";
import {AnalyticsAction, useAnalyticsStore} from "@/store/analytics.store";

const MAXIMUM_CHUNK_LENGTH_SECONDS = 90;
const MAXIMUM_TEXT_ANSWER_LENGTH = 4096;

export interface OnSurveyAgentViewFinished {
    recordedAudio: Blob;
    conversationalSurveyRounds?: ConversationalSurveyRound[];
    isTextSurvey: boolean;
}

enum RecordingState {
    NotRecording = 0, Initiated, InProgress
}

interface Data {
    conversationalSurveyRounds?: ConversationalSurveyRound[];
    recordingManager: RecordingManager;
    conversationalSurveyService: ConversationalSurveyService;
    isUsersTurn: boolean;
    isMicrophoneTerminallyUnavailable: boolean;
    microphoneUnavailableError: boolean;
    recordingState: RecordingState;
    conversationalSurveyTerminalError?: unknown;
    showNPSRating: boolean;
    showCSATRating: boolean;
    maxChunkLengthSeconds: number;
    isTextSurvey?: boolean;
    canSwitchSurveyState: boolean;
    answerExists: boolean;
    isShowingFollowUpQuestionInfo: boolean;
    followupQuestion?: string;
    loaderMessage?: string;
    didShowLoaderMessage: boolean;
    agentMessage?: string;
    questionCounter: number
}

export default defineComponent({
    props: {
        initialQuestion: {
            type: String,
            required: true
        },
        combinedSurveyQuestion: {
            type: String,
            required: true,
        },
        campaign: {
            required: true,
            type: Object as PropType<Campaign>
        },
        rating: {
            type: Number,
        },
    },
    data(): Data {
        return {
            recordingManager: undefined as unknown as RecordingManager,
            conversationalSurveyService: undefined as unknown as ConversationalSurveyService,
            isUsersTurn: true,
            isMicrophoneTerminallyUnavailable: false,
            microphoneUnavailableError: false,
            recordingState: RecordingState.NotRecording as RecordingState,
            conversationalSurveyRounds: undefined,
            conversationalSurveyTerminalError: undefined,
            showNPSRating: false,
            showCSATRating: false,
            maxChunkLengthSeconds: MAXIMUM_CHUNK_LENGTH_SECONDS,
            isTextSurvey: undefined,
            canSwitchSurveyState: true,
            answerExists: false,
            isShowingFollowUpQuestionInfo: false,
            followupQuestion: undefined,
            loaderMessage: undefined,
            didShowLoaderMessage: false,
            agentMessage: undefined,
            questionCounter: 1,
        }
    },
    mixins: [BackgroundImageMixin],
    watch: {
        initialQuestion: {
            immediate: true,
            handler() {
                this.agentMessage = this.initialQuestion;
            }
        },
        // this should only happen when the campaign is in preview mode
        textFieldDefault: {
            immediate: true,
            handler() {
                this.isTextSurvey = this.textFieldDefault;
            },
        },
    },
    mounted() {
        if (!this.campaign.conversationalSurvey) {
            this.maxChunkLengthSeconds = this.campaign.maximumRecordingDuration || MAXIMUM_CHUNK_LENGTH_SECONDS;
        }

        this.recordingManager = new RecordingManager({
            onRecordingTerminallyFailed: this.onRecordingTerminallyFailed,
            onRecordingTerminated: this.onRecordingTerminated,
            onChunkLengthUpdate: this.onChunkLengthUpdate,
        });

        let ratingForConversationSurvey: string | null = null;
        if (this.rating && this.campaign.isNPSCampaign) {
            ratingForConversationSurvey = `User gave NPS rating: ${this.rating}`;
        } else if (this.rating && this.campaign.isCSATCampaign) {
            ratingForConversationSurvey = `User gave CSAT rating: ${this.rating}`;
        }

        this.conversationalSurveyService = new ConversationalSurveyService({
            isCoreporter: !!this.$route.query.coreporter,
            isCoreporterAdvanced: !!this.$route.query.useAdvanced,
            campaignID: this.campaign.id,
            initialQuestion: this.combinedSurveyQuestion,
            rating: ratingForConversationSurvey,
            onQuestionUpdated: (resp: ConversationalSurveyQuestionUpdated): void => {
                this.showCSATRating = false;
                this.showNPSRating = false;

                // always store the followup question, even if we're not showing it
                this.followupQuestion = resp.question;
            },
            onQuestionFinalized: (): void => {
                this.isUsersTurn = true;
                this.isShowingFollowUpQuestionInfo = false;
                if (this.followupQuestion) {
                    this.didShowLoaderMessage = true;

                    this.agentMessage = undefined;
                    setTimeout(() => {
                        this.agentMessage = this.followupQuestion;
                        this.setAnswerFieldFocus();
                    }, 400);
                }

                this.trackEvent('creationConversationalSurveyQuestionAsked', {
                    ...this.$analytics.campaignParams(this.campaign),
                    question:this.agentMessage,
                });

                this.answerExists = false;
            },
            onSurveyEnded: (rounds: ConversationalSurveyRound[]): void => {
                this.conversationalSurveyRounds = rounds;
                if (this.isTextSurvey) {
                    this.finishedWithTextSurvey(this.conversationalSurveyRounds);
                } else {
                    this.recordingManager.terminate();
                }
            }
        })

        this.agentMessage = this.initialQuestion;
        if (this.rating !== undefined) {
            if (this.campaign.isNPSCampaign) {
                this.showNPSRating = true;
            } else if (this.campaign.isCSATCampaign) {
                this.showCSATRating = true;
            }
        }

        if (this.campaign.textFieldDefault) {
            this.switchAnswerStateToText();
        } else {
            this.switchAnswerStateToAudio();
        }
    },
    computed: {
        textFieldDefault(): boolean {
            return this.campaign.textFieldDefault;
        },
        campaignBrandingManager() {
            return campaignBrandingManager
        },
        isRecorderLoading() {
            return this.recordingState === RecordingState.Initiated;
        },
        isRecordingInProgress() {
            return this.recordingState === RecordingState.InProgress;
        },
        isRecordingReady(): boolean {
            return this.recordingState === RecordingState.NotRecording;
        },
        getRatingTitle(): string | null {
            switch (this.rating) {
                case 1:
                    return this.$t('creation.CSAT1');
                case 2:
                    return this.$t('creation.CSAT2');
                case 3:
                    return this.$t('creation.CSAT3');
                case 4:
                    return this.$t('creation.CSAT4');
                case 5:
                    return this.$t('creation.CSAT5');
                default:
                    return null;
            }
        },

        // Microphone state is visible when:
        // 1. It is NOT a text survey
        // 2. The microphone is not terminally unavailable
        // 3. It is the user's turn
        showMicrophoneState(): boolean {
            if (this.isTextSurvey === undefined) {
                return false;
            }

            return !this.isTextSurvey && !this.isMicrophoneTerminallyUnavailable && this.isUsersTurn;
        },
        // Show the audio enable button when:
        // 1. It is a text survey
        // 2. There is no text in the answer field (i.e. the user has not typed anything)
        // 3. User can still switch (i.e. the user has not started recording or did not submit the first answer)
        // 4. It is the user's turn
        // 5. Microphone is not available (i.e. the user is on the device without microphone)
        showAudioEnableButton(): boolean {
            if (this.isTextSurvey === undefined) {
                return false;
            }

            return this.isTextSurvey && !this.answerExists && this.canSwitchSurveyState && this.isUsersTurn && !this.microphoneUnavailableError;
        },
        // Show the text enable button when:
        // 1. It is a text survey
        // 2. User can still switch (i.e. the user has not started recording or did not submit the first answer)
        // 3. It is the user's turn
        showTextEnableButton(): boolean {
            if (this.isTextSurvey === undefined) {
                return false;
            }

            return !this.isTextSurvey && this.canSwitchSurveyState && this.isUsersTurn;
        },
        // Show the text send button when:
        // 1. It is a text survey
        // 2. It is the user's turn
        // 3. There is text in the answer field (i.e. the user has typed something)
        showTextSendButton(): boolean {
            if (this.isTextSurvey === undefined) {
                return false;
            }

            return this.isTextSurvey && this.isUsersTurn && this.answerExists;
        },
        // Show the text field when:
        // 1. It is a text survey
        // 2. It is the user's turn
        // 3. Is not showing follow-up question info
        showTextField(): boolean {
            if (this.isTextSurvey === undefined) {
                return false;
            }

            return this.isTextSurvey;
            // return !this.isShowingFollowUpQuestionInfo && this.isTextSurvey && this.isUsersTurn;
        },
        // Show the text placeholder when:
        // 1. Show the text field
        // 2. There is no text in the answer field (i.e. the user has not typed anything)
        showTextPlaceholder(): boolean {
            return this.showTextField && !this.answerExists;
        },

        // Show the follow-up question loader when:
        // 1. Is showing follow-up question info
        showLoader(): boolean {
            return this.isShowingFollowUpQuestionInfo && !this.isUsersTurn;
        },
    },
    methods: {
        // analytics store
        ...mapActions(useAnalyticsStore, ['trackPageView']),
        ...mapActions(useAnalyticsStore, ['trackEvent']),
        ...mapActions(useAnalyticsStore, ['sendAnalyticsEvent']),

        async recordingButtonTapped() {
            if (this.recordingState === RecordingState.InProgress) {
                this.canSwitchSurveyState = false;
                await this.pauseRecording();
            } else {
                await this.resumeRecording();
            }
        },
        async pauseRecording(): Promise<void> {
            this.trackEvent('creationRecorderPaused', {
                ...this.$analytics.campaignParams(this.campaign),
            });

            if (this.questionCounter === 1) {
                this.sendAnalyticsEvent(AnalyticsAction.VoiceAnswerSubmitted, this.campaign.id);
            } else if (this.questionCounter === 2) {
                this.sendAnalyticsEvent(AnalyticsAction.SecondVoiceAnswerSubmitted, this.campaign.id);
            }

            this.recordingState = RecordingState.NotRecording;
            this.isUsersTurn = false;

            const timestamp = this.recordingManager.pauseRecording();
            this.questionCounter++;

            if (this.campaign.conversationalSurvey && !this.conversationalSurveyTerminalError) {
                try {
                    this.showFollowupQuestion();
                    return await this.conversationalSurveyService.stopListening(timestamp);
                } catch (e) {
                    console.log("Unable to generate follow up question, terminating the survey", e)
                }
            }
            this.recordingManager.terminate();
        },
        async resumeRecording() {
            this.recordingState = RecordingState.Initiated;
            const mediaStream = await this.recordingManager.record();
            if (this.campaign.conversationalSurvey) {
                try {
                    await this.conversationalSurveyService.listenToNextAnswerAndRespond(mediaStream);
                } catch (e) {
                    this.conversationalSurveyTerminalError = e;
                    console.error("Unable to begin a conversational survey, falling back to single-question survey", e)
                }
            }

            this.recordingState = RecordingState.InProgress;
            this.trackEvent('creationRecorderResumed', {
                ...this.$analytics.campaignParams(this.campaign),
            });
        },
        onRecordingTerminallyFailed(e: Error): void {
            console.error("Recording failed", e)
            this.isMicrophoneTerminallyUnavailable = true;
            this.recordingState = RecordingState.NotRecording

            let specificError: string | undefined = undefined;
            switch (e) {
                case RecordingUnavailableNoCapabilitiesError:
                    this.microphoneUnavailableError = true;
                    this.switchAnswerStateToText();
                    break;
                case RecordingUnavailableBrowserNoMicrophoneError:
                    this.microphoneUnavailableError = true;
                    this.switchAnswerStateToText();
                    break;
                case RecordingUnavailableDenied:
                    specificError = this.$t('survey.microphoneErrors.deniedError')
                    break;
                case RecordingUnavailableAnotherAppRecording:
                    specificError = this.$t('survey.microphoneErrors.anotherAppRecordingError')
                    break;
                case RecordingUnavailableMicrophoneIsMuted:
                    specificError = this.$t('survey.microphoneErrors.mutedMicrophoneError')
                    break;
                default:
                    this.microphoneUnavailableError = true;
                    this.switchAnswerStateToText();
                    break;
            }

            if (specificError !== undefined) {
                this.agentMessage += " " + specificError;
            }

            this.trackEvent('creationRecordingTerminallyFailed', {
                ...this.$analytics.campaignParams(this.campaign),
                error: e,
            });
        },
        onRecordingTerminated(blob: Blob): void {
            this.trackEvent('creationRecorderTerminated', {
                ...this.$analytics.campaignParams(this.campaign),
            });
            console.log("onRecordingTerminated called", blob, this.conversationalSurveyRounds)
            this.$emit("finished", {
                recordedAudio: blob,
                conversationalSurveyRounds: this.conversationalSurveyRounds,
                isTextSurvey: this.isTextSurvey,
            } as OnSurveyAgentViewFinished);
        },
        onChunkLengthUpdate(length: number): void {
            if (length >= this.maxChunkLengthSeconds * 1000) {
                if (this.recordingState === RecordingState.InProgress) {
                    this.trackEvent('creationRecorderAutoPauseInitiated', {
                        ...this.$analytics.campaignParams(this.campaign),
                    });
                    this.pauseRecording()
                }
            }
        },

        finishedWithTextSurvey(rounds: ConversationalSurveyRound[]): void {
            this.trackEvent('textSurveyFinished', {
                ...this.$analytics.campaignParams(this.campaign),
            });
            this.$emit("finished", {
                conversationalSurveyRounds: rounds,
                isTextSurvey: this.isTextSurvey,
            } as OnSurveyAgentViewFinished);
        },

        isCSATStarSelected(rate: number): boolean {
            return this.rating !== undefined && this.rating >= rate;
        },

        sendTextMessage(): void {
            const answer = (this.$refs.answerText as HTMLElement).innerText.trim();
            if (!answer || !this.isUsersTurn) {
                return;
            }

            this.isUsersTurn = false;

            if (this.questionCounter === 1) {
                this.sendAnalyticsEvent(AnalyticsAction.TextAnswerSubmitted, this.campaign.id);
            } else if (this.questionCounter === 2) {
                this.sendAnalyticsEvent(AnalyticsAction.SecondTextAnswerSubmitted, this.campaign.id);
            }

            this.questionCounter++;

            if (this.campaign.conversationalSurvey) {
                this.canSwitchSurveyState = false;
                this.showFollowupQuestion();
                this.conversationalSurveyService.getNextQuestion(answer);
            } else {
                this.finishedWithTextSurvey([{
                    question: this.combinedSurveyQuestion,
                    questionTimestamp: 0,
                    answer: answer,
                    answerLength: 0,
                }]);
            }
        },

        showFollowupQuestion(): void {
            if (this.campaign.conversationalSurvey) {
                this.isShowingFollowUpQuestionInfo = true;
                this.loaderMessage = this.$t('survey.followupQuestionsInfo');
            }
        },

        switchAnswerStateToAudio(): void {
            this.isTextSurvey = false;
            this.answerExists = false;
        },
        switchAnswerStateToText(): void {
            this.isTextSurvey = true;
            this.recordingState = RecordingState.NotRecording;
            this.recordingManager.reset();
            this.setAnswerFieldFocus();
        },

        onInput(): void {
            const element: HTMLElement | null = this.$refs.answerText as HTMLElement;
            if (element) {
                const answerTextElement = this.$refs.answerText as HTMLElement;
                const value = answerTextElement.innerText.trim();
                this.answerExists = !!value;
                if (value.length > MAXIMUM_TEXT_ANSWER_LENGTH) {
                    answerTextElement.innerText = value.substring(0, MAXIMUM_TEXT_ANSWER_LENGTH);
                    answerTextElement.focus();

                    // set the cursor position to the end of the text
                    const range = document.createRange();
                    range.selectNodeContents(answerTextElement);
                    range.collapse(false);
                    const selection = window.getSelection();
                    selection?.removeAllRanges();
                    selection?.addRange(range);
                }
            }
        },

        onEnterPressed(event: KeyboardEvent): void {
            event.preventDefault();
            if (this.isTextSurvey) {
                this.sendTextMessage();
            }
        },

        setAnswerFieldFocus(): void {
            if (this.isTextSurvey) {
                const answerField = this.$refs.answerText as HTMLElement;
                if (answerField) {
                    answerField.focus();
                } else {
                    setTimeout(() => {
                        this.setAnswerFieldFocus();
                    }, 10)
                }
            }
        }
    }
});


export function totalDuration(rounds: ConversationalSurveyRound[]): number {
    return rounds.reduce((acc, recording) => acc + recording.answerLength, 0);
}
